import React, { useState, useRef, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTenantFromUrl } from '../hooks/useTenantFromUrl';
import { useDebouncedSearch } from '../hooks/useDebouncedSearch';
import { useInfiniteScroll } from '../hooks/useInfiniteScroll';
import { contactAPI } from '../services/api';
import PageHeader from './PageHeader';
import DeleteIconButton from './DeleteIconButton';
import ContactForm from './ContactForm';
import { hasPermission } from '../utils/permissions';
import './ListWithDetail.css';
import './ContactList.css';

const PAGE_SIZE = 25;

function ContactList() {
  const navigate = useNavigate();
  const { id: detailId } = useParams();
  const { tenantPath } = useTenantFromUrl();
  const [contacts, setContacts] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(PAGE_SIZE);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [searchParams] = useSearchParams();
  const [contactSearch, setContactSearch] = useState(searchParams.get('q') || '');
  const [phoneSearch, setPhoneSearch] = useState('');
  const sentinelRef = useRef(null);
  const filtersRef = useRef({ contactSearch, phoneSearch });
  filtersRef.current = { contactSearch, phoneSearch };
  const page1RequestIdRef = useRef(0);

  const canCreateContact = hasPermission('contacts.create');
  const canDeleteContact = hasPermission('contacts.delete');

  const loadContacts = useCallback(async (pageNum = 1, append = false) => {
    const snap = { ...filtersRef.current };
    let page1Id;
    if (pageNum === 1 && !append) {
      page1Id = ++page1RequestIdRef.current;
    }
    try {
      if (pageNum === 1 && !append) setLoading(true);
      else setLoadingMore(true);
      const params = { page: pageNum, pageSize };
      if (snap.contactSearch.trim()) params.q = snap.contactSearch.trim();
      if (snap.phoneSearch.trim()) params.phone_q = snap.phoneSearch.trim();
      const response = await contactAPI.getAll(params);
      if (pageNum === 1 && !append) {
        if (page1Id !== page1RequestIdRef.current) return;
      } else if (append) {
        const cur = filtersRef.current;
        if (snap.contactSearch !== cur.contactSearch || snap.phoneSearch !== cur.phoneSearch) return;
      }
      const data = response.data.data ?? [];
      const newTotal = response.data.total ?? 0;
      if (append) setContacts((prev) => [...prev, ...data]);
      else setContacts(data);
      setTotal(newTotal);
      setPage(pageNum);
    } catch (error) {
      if (pageNum === 1 && !append && page1Id !== page1RequestIdRef.current) return;
      if (append) {
        const cur = filtersRef.current;
        if (snap.contactSearch !== cur.contactSearch || snap.phoneSearch !== cur.phoneSearch) return;
      }
      console.error('Error loading contacts:', error);
      alert('Failed to load contacts');
    } finally {
      if (pageNum === 1 && !append) {
        if (page1Id === page1RequestIdRef.current) setLoading(false);
      } else {
        setLoadingMore(false);
      }
    }
  }, [pageSize]);

  useDebouncedSearch(`${contactSearch}|${phoneSearch}`, () => loadContacts(1));

  const hasMore = contacts.length < total && total > 0;
  const loadMore = () => { if (!loading && !loadingMore && hasMore) loadContacts(page + 1, true); };
  useInfiniteScroll(sentinelRef, loadMore, hasMore, loading || loadingMore);

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this contact?')) {
      return;
    }

    try {
      await contactAPI.delete(id);
      if (String(id) === String(detailId)) navigate(tenantPath('/lists/contacts'));
      setContacts((prev) => prev.filter((c) => c.id !== id));
      setTotal((t) => Math.max(0, t - 1));
    } catch (error) {
      console.error('Error deleting contact:', error);
      alert('Failed to delete contact');
    }
  };

  const colCount = 2 + (canDeleteContact ? 1 : 0);
  const showPanel = detailId != null;

  return (
    <div className={`list-with-detail ${showPanel ? 'list-with-detail--panel-open' : ''}`}>
    <div className="contact-list list-with-detail__list">
      <div className="header">
        <PageHeader title="Contacts">
          {canCreateContact && (
            <button onClick={() => navigate(tenantPath('/lists/contacts/new'))} className="btn-new">+ New Contact</button>
          )}
        </PageHeader>
      </div>

      <div className="contact-list-pagination-info">
        {loading && contacts.length === 0 ? 'Loading...' : total === 0 ? 'No contacts found' : total === 1 ? '1 contact found' : `${total} contacts found`}
      </div>

      <div className="contact-list-table-wrap list-table-wrap">
        <table className="contact-list-table list-table">
          <thead>
            <tr className="list-header-labels">
              <th>Contact</th>
              <th>Phone</th>
              {canDeleteContact && <th>Delete</th>}
            </tr>
            <tr className="list-filter-row contact-list-filter-row">
              <th>
                <input
                  type="text"
                  className="contact-list-filter-input"
                  placeholder="Search contact..."
                  value={contactSearch}
                  onChange={(e) => setContactSearch(e.target.value)}
                  aria-label="Filter by contact name"
                />
              </th>
              <th>
                <input
                  type="text"
                  className="contact-list-filter-input"
                  placeholder="Search phone..."
                  value={phoneSearch}
                  onChange={(e) => setPhoneSearch(e.target.value)}
                  aria-label="Filter by phone"
                />
              </th>
              {canDeleteContact && <th />}
            </tr>
          </thead>
          <tbody>
            {loading && contacts.length === 0 ? (
              <tr><td colSpan={colCount} className="no-data loading-cell">Loading contacts...</td></tr>
            ) : contacts.length === 0 ? (
              <tr><td colSpan={colCount} className="no-data">No contacts found</td></tr>
            ) : (
              contacts.map((contact) => {
                const contactName = [contact.last_name, contact.first_name].filter(Boolean).join(', ') || '—';
                return (
                  <tr key={contact.id} onClick={() => navigate(tenantPath(`/lists/contacts/${contact.id}`))} className="clickable-row">
                    <td>{contactName}</td>
                    <td>{contact.phone || '—'}</td>
                    {canDeleteContact && (
                      <td onClick={(e) => e.stopPropagation()}>
                        <DeleteIconButton className="list-btn-delete" onClick={() => handleDelete(contact.id)} />
                      </td>
                    )}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {hasMore && <div ref={sentinelRef} className="infinite-scroll-sentinel" aria-hidden="true" />}
      {loadingMore && <div className="infinite-scroll-loading">Loading more...</div>}
    </div>

    {showPanel && (
      <div className="list-with-detail__panel">
        <ContactForm />
      </div>
    )}
    </div>
  );
}

export default ContactList;
